import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { saveToken } from '../utils/getToken';
import Stereo from '../images/stetoscope.png';

const baseURL = process.env.REACT_APP_BASE_URL || 'https://api.quickcare.ng/admin';

const Login = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage('');
        setMessageType('');
        
        // Remove all spaces from input values before validation
        const cleanedUsername = username.replace(/\s/g, '');
        const cleanedPassword = password.replace(/\s/g, '');
        
        if (!cleanedUsername) {
            setMessage('Username is required');
            setMessageType('error');
            return;
        }
        
        if (!cleanedPassword) {
            setMessage('Password is required');
            setMessageType('error');
            return;
        }
        
        setIsLoading(true);

        const myHeaders = new Headers();
        myHeaders.append("Content-Type", "application/x-www-form-urlencoded");
        myHeaders.append("Accept", "application/json");

        const urlencoded = new URLSearchParams();
        urlencoded.append("username", cleanedUsername);
        urlencoded.append("password", cleanedPassword);

        const requestOptions = {
            method: "POST",
            headers: myHeaders,
            body: urlencoded,
            redirect: "follow"
        };

        try {
            const response = await fetch(`${baseURL}/auth/login`, requestOptions);
            const result = await response.json();

            if (response.ok) {
                const token = result.access_token;
                if (token) {
                    saveToken(token);
                    setMessage('Login successful! Redirecting...');
                    setMessageType('success');
                    
                    setTimeout(() => {
                        navigate('/overview');
                    }, 1500);
                } else {
                    setMessage('Login failed: No token received');
                    setMessageType('error');
                }
            } else {
                const errorMessage = result.detail || result.message || 'Login failed. Please try again.';
                setMessage(errorMessage);
                setMessageType('error');
            }
        } catch (error) {
            setMessage('Network error. Please check your connection and try again.');
            setMessageType('error');
        } finally {
            setIsLoading(false);
        }
    };

    // Handle input change and prevent spaces
    const handleUsernameChange = (e) => {
        const value = e.target.value.replace(/\s/g, '');
        setUsername(value);
    };

    const handlePasswordChange = (e) => {
        const value = e.target.value.replace(/\s/g, '');
        setPassword(value);
    };

    return (
        <div className='min-h-screen w-full flex flex-col lg:flex-row bg-gradient-to-br from-[#f0f9f0] to-[#e8f5e9]'>
            {/* Left Section - Login Form */}
            <div className='w-full lg:w-1/2 flex items-center justify-center p-6 lg:p-12'>
                <div className='w-full max-w-md mx-auto'>
                    {/* Logo */}
                    <Link to='/' className='inline-block mb-8'>
                        <div className='flex items-center gap-2'>
                            <div className='w-10 h-10 bg-[#069125] rounded-lg flex items-center justify-center text-white text-xl font-bold'>
                                QC
                            </div>
                            <span className='text-2xl font-bold text-[#069125]'>QuickCare</span>
                        </div>
                    </Link>

                    {/* Welcome Section */}
                    <div className='mb-8'>
                        <h1 className='text-3xl lg:text-4xl font-bold text-gray-800'>
                            Welcome Back
                        </h1>
                        <p className='text-gray-600 mt-2'>
                            Login to access your admin dashboard
                        </p>
                    </div>

                    {/* Message Display */}
                    {message && (
                        <div className={`mb-6 p-4 rounded-lg text-sm flex items-start gap-2 ${
                            messageType === 'success' 
                                ? 'bg-green-50 text-green-700 border border-green-200' 
                                : 'bg-red-50 text-red-700 border border-red-200'
                        }`}>
                            <span className='mt-0.5 text-lg'>
                                {messageType === 'success' ? '✓' : '⚠'}
                            </span>
                            <span>{message}</span>
                        </div>
                    )}

                    {/* Login Form */}
                    <form onSubmit={handleSubmit} className='space-y-6'>
                        {/* Username Field */}
                        <div className='relative'>
                            <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400 text-lg'>
                                👤
                            </div>
                            <input
                                type="text"
                                className='w-full pl-10 pr-3 py-3 bg-white border border-gray-200 rounded-xl focus:border-[#069125] focus:ring-2 focus:ring-[#069125]/20 outline-none transition-all duration-200 text-gray-800 placeholder-gray-400'
                                placeholder='Username or ID number'
                                value={username}
                                onChange={handleUsernameChange}
                                required
                                autoComplete='username'
                                spellCheck={false}
                            />
                        </div>

                        {/* Password Field */}
                        <div className='relative'>
                            <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400 text-lg'>
                                🔒
                            </div>
                            <input
                                type={showPassword ? "text" : "password"}
                                className='w-full pl-10 pr-12 py-3 bg-white border border-gray-200 rounded-xl focus:border-[#069125] focus:ring-2 focus:ring-[#069125]/20 outline-none transition-all duration-200 text-gray-800 placeholder-gray-400'
                                placeholder='Password'
                                value={password}
                                onChange={handlePasswordChange}
                                required
                                autoComplete='current-password'
                                spellCheck={false}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className='absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 text-lg'
                            >
                                {showPassword ? '👁️' : '👁️‍🗨️'}
                            </button>
                        </div>

                        {/* Remember Me & Forgot Password */}
                        <div className='flex items-center justify-between'>
                            <label className='flex items-center gap-2 text-sm text-gray-600 cursor-pointer'>
                                <input 
                                    type="checkbox" 
                                    className='w-4 h-4 text-[#069125] border-gray-300 rounded focus:ring-[#069125] outline-none'
                                />
                                <span>Remember me</span>
                            </label>
                            <Link className='text-sm text-[#069125] hover:text-[#057a1f] font-medium hover:underline'>
                                Forgot Password?
                            </Link>
                        </div>

                        {/* Login Button */}
                        <button
                            type='submit'
                            disabled={isLoading}
                            className={`w-full py-3 rounded-xl font-semibold text-white transition-all duration-200 flex items-center justify-center gap-2 ${
                                isLoading 
                                    ? 'bg-gray-400 cursor-not-allowed' 
                                    : 'bg-[#069125] hover:bg-[#057a1f] hover:shadow-lg hover:shadow-[#069125]/30 active:transform active:scale-[0.98]'
                            }`}
                        >
                            {isLoading ? (
                                <>
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                    <span>Logging in...</span>
                                </>
                            ) : (
                                <>
                                    <span className='text-lg'>🔑</span>
                                    <span>Login</span>
                                </>
                            )}
                        </button>
                    </form>

                    {/* Footer */}
                    <div className='mt-8 text-center'>
                        <p className='text-sm text-gray-500'>
                            © {new Date().getFullYear()} QuickCare. All rights reserved.
                        </p>
                    </div>
                </div>
            </div>

            {/* Right Section - Image */}
            <div className='hidden lg:flex w-1/2 relative overflow-hidden bg-gradient-to-br from-[#069125] to-[#057a1f]'>
                <img 
                    src={Stereo} 
                    alt="Stethoscope" 
                    className='w-full h-full object-cover object-center'
                />
                {/* Optional: Overlay gradient for better text visibility if needed */}
                <div className='absolute inset-0 bg-gradient-to-t from-black/20 to-transparent'></div>
            </div>
        </div>
    );
};

export default Login;